'use client'

// ============================================================================
// NEİZLESEM - Favorites (localStorage, no login required)
// ============================================================================

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import type { ContentItem, FavoriteItem } from '@/lib/types'

const STORAGE_KEY = 'neizlesem:favorites:v1'

interface FavoritesContextValue {
  favorites: FavoriteItem[]
  isFavorite: (id: string) => boolean
  toggleFavorite: (item: ContentItem | FavoriteItem) => void
  removeFavorite: (id: string) => void
  ready: boolean
}

const FavoritesContext = createContext<FavoritesContextValue | null>(null)

function toFavorite(item: ContentItem | FavoriteItem): FavoriteItem {
  return {
    id: item.id,
    type: item.type,
    title: item.title,
    poster: item.poster ?? null,
    year: item.year ?? null,
    rating: item.rating ?? null,
    genres: item.genres ?? [],
  }
}

export function FavoritesProvider({ children }: { children: React.ReactNode }) {
  const [favorites, setFavorites] = useState<FavoriteItem[]>([])
  const [ready, setReady] = useState(false)

  // Load once on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) setFavorites(JSON.parse(raw))
    } catch (e) {
      console.error('[favorites] load error', e)
    }
    setReady(true)
  }, [])

  // Persist on change (after ready to avoid clobbering initial load)
  useEffect(() => {
    if (!ready) return
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites))
    } catch (e) {
      console.error('[favorites] save error', e)
    }
  }, [favorites, ready])

  const isFavorite = useCallback(
    (id: string) => favorites.some((f) => f.id === id),
    [favorites]
  )

  const toggleFavorite = useCallback((item: ContentItem | FavoriteItem) => {
    setFavorites((prev) => {
      if (prev.some((f) => f.id === item.id)) {
        return prev.filter((f) => f.id !== item.id)
      }
      return [toFavorite(item), ...prev]
    })
  }, [])

  const removeFavorite = useCallback((id: string) => {
    setFavorites((prev) => prev.filter((f) => f.id !== id))
  }, [])

  const value = useMemo(
    () => ({ favorites, isFavorite, toggleFavorite, removeFavorite, ready }),
    [favorites, isFavorite, toggleFavorite, removeFavorite, ready]
  )

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>
}

export function useFavorites(): FavoritesContextValue {
  const ctx = useContext(FavoritesContext)
  if (!ctx) {
    throw new Error('useFavorites must be used within FavoritesProvider')
  }
  return ctx
}
